import React, { useEffect, useState } from "react";
import {
  AvatarGroup,
  Box,
  Chip,
  IconButton,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
} from "@mui/material";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  setIsLoading,
  setIsPaginationOrSearchClicked,
  setSelectedQueryName,
} from "../../../../features/dataStoreReqSlice";
import { useDispatch } from "react-redux";
import {
  selectUserData,
  selectUserToken,
} from "../../../../features/userSlice";
import { DataStoreReqEndPointLogic } from "../../../../logic/Admin/DataStoreRequest/DataStoreRequestEndPointLogic";
import { CheckPermissions } from "../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import {
  fetchBizLayerQuery,
  formatUtcToHumanReadable,
} from "../DataStoreHelpers/dataStoreHelpers";
import DeleteConfirmationDialog from "./DeleteDialog";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";

interface Props {
  tableHeadings: { [key: string]: string };
}

const DataStoreRequestTable = ({ tableHeadings }: Props) => {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = Number(searchParams.get("page")) || 0;
  const [page, setPage] = useState(pageParam);
  const dispatch = useDispatch();
  const token = useSelector(selectUserToken);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const queryContent =
    useSelector((state: any) => state.dataStoreReq.quaryContent) || [];
  const {
    selectedDatabase,
    selectedType,
    selectedId,
    selectedQueryName,
    isPaginationOrSearchClicked,
    selectedModules,
  } = useSelector((state: any) => state.dataStoreReq);
  const response = useSelector(selectUserData);

  const hasDataStoreEditPermission = CheckPermissions(
    response?.data?.products?.admin,
    "datastore_requests",
    "edit_datastore_requests",
  );

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      updatedParams.set("page", newPage.toString());
      return updatedParams;
    });
    dispatch(setIsPaginationOrSearchClicked(true));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const filteredTemplateData = queryContent.filter((query: any) => {
    const databaseFeatures =
      selectedDatabase && selectedDatabase.length !== 0
        ? selectedDatabase.includes(query?.database)
        : true; // If no Asset is selected, treat it as a match

    const typeFeatures =
      selectedType && selectedType?.length !== 0
        ? selectedType.includes(query?.type)
        : true; // If no Asset is selected, treat it as a match

    const idFeatures =
      selectedId && selectedId?.length !== 0
        ? selectedId.includes(String(query?.id))
        : true;

    const queryNameFeatures =
      selectedQueryName && selectedQueryName.length !== 0
        ? selectedQueryName === query?.name
        : true;

    const moduleNameFeatures =
      selectedModules && selectedModules.length !== 0
        ? query?.modules?.some((module: any) =>
            selectedModules.includes(module),
          )
        : true;

    return (
      databaseFeatures &&
      typeFeatures &&
      queryNameFeatures &&
      idFeatures &&
      moduleNameFeatures
    );
  });

  const sortedData = [...filteredTemplateData].sort((a, b) => {
    if (!orderBy) return 0;

    if (orderBy === "modules") {
      const aValue = Array.isArray(a[orderBy]) ? a[orderBy].join(", ") : "";
      const bValue = Array.isArray(b[orderBy]) ? b[orderBy].join(", ") : "";

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    }
    if (orderBy === "modified" || orderBy === "created") {
      const aValue = a[orderBy] ? new Date(a[orderBy]).getTime() : 0;
      const bValue = b[orderBy] ? new Date(b[orderBy]).getTime() : 0;

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    }

    const aValue =
      typeof a[orderBy] === "string" ? a[orderBy]?.toLowerCase() : "";
    const bValue =
      typeof b[orderBy] === "string" ? b[orderBy]?.toLowerCase() : "";

    if (aValue < bValue) return order === "asc" ? -1 : 1;
    if (aValue > bValue) return order === "asc" ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const shouldResetPage =
    page !== 0 &&
    !isPaginationOrSearchClicked &&
    (selectedDatabase || selectedType || selectedQueryName || selectedModules);

  useEffect(() => {
    if (
      page !== 0 &&
      !isPaginationOrSearchClicked &&
      (selectedDatabase || selectedType || selectedQueryName || selectedModules)
    ) {
      setPage(0);
    }
    // eslint-disable-next-line
  }, [shouldResetPage]);

  const handleDelete = async (id: string) => {
    if (!id) return;

    try {
      await DataStoreReqEndPointLogic.deleteBizLayerQuery(token, dispatch, id);
      // Fetch updated data
      fetchBizLayerQuery(token, dispatch, setIsLoading);

      // Remove query params
      searchParams.delete("search");
      searchParams.delete("id");
      dispatch(setSelectedQueryName("")); // Reset setSelectedQueryName

      setSearchParams(searchParams); // Update the URL
    } catch (error) {
      console.error("Error deleting query:", error);
    }
  };

  const handleDeleteClick = (id: any) => {
    setItemToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (itemToDelete) {
      handleDelete(itemToDelete); // Your existing delete function
    }
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  return (
    <>
      <TableContainer className="PageScantablewrap">
        <Table aria-label="collapsible table" size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {Object.entries(tableHeadings).map(([key, value], index) => (
                <TableCell key={index} align="left">
                  {key !== "quick_actions" ? (
                    <TableSortLabel
                      active={orderBy === key}
                      direction={orderBy === key ? order : "asc"}
                      onClick={() => handleRequestSort(key)}
                    >
                      {value}
                    </TableSortLabel>
                  ) : (
                    value
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.length > 0 ? (
              sortedData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row: any, rowIndex: number) => (
                  <TableRow key={rowIndex}>
                    {Object.keys(tableHeadings).map((key, cellIndex) => (
                      <TableCell key={cellIndex} align="left">
                        {key === "name" ? (
                          <RouterLink
                            data-tracker="239"
                            to={{
                              pathname: "/admin/queries/query_view",
                              search: (() => {
                                const newParams = new URLSearchParams(
                                  window.location.search,
                                );
                                newParams.set("id", row.id); // Add or update 'queryId'
                                return `?${newParams.toString()}`;
                              })(),
                            }}
                            style={{
                              textDecoration: "underline",
                              color: "#9C27B0",
                            }}
                          >
                            {row[key] || ""}
                          </RouterLink>
                        ) : key === "modules" ? (
                          <div className="chip-data">
                            {Array.isArray(row?.modules) &&
                              row.modules.length > 0 && (
                                <>
                                  {/* Display first chip */}
                                  <Chip
                                    label={row.modules[0]}
                                    sx={{
                                      backgroundColor: "#7B1FA2",
                                      color: "#ffffff",
                                      height: "26px",
                                    }}
                                  />
                                  {/* Display second chip if available */}
                                  {row.modules.length > 1 && (
                                    <Chip
                                      label={row.modules[1]}
                                      sx={{
                                        backgroundColor: "#7B1FA2",
                                        color: "#ffffff",
                                        height: "26px",
                                      }}
                                    />
                                  )}
                                  {/* AvatarGroup or indicator for remaining items */}
                                  {row.modules.length > 2 && (
                                    <AvatarGroup
                                      total={row.modules.length - 2}
                                    />
                                  )}
                                </>
                              )}
                          </div>
                        ) : key === "quick_actions" ? (
                          <Box display="flex" gap={1}>
                            {hasDataStoreEditPermission ? (
                              <Tooltip title="Edit" placement="top">
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  component={RouterLink}
                                  to={{
                                    pathname:
                                      "/admin/queries/edit_query",
                                    search: (() => {
                                      const currentParams = new URLSearchParams(
                                        window.location.search,
                                      );
                                      currentParams.set("id", row.id);
                                      return currentParams.toString();
                                    })(),
                                  }}
                                  data-tracker="241"
                                >
                                  <CreateOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Edit" placement="top">
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  disabled
                                >
                                  <CreateOutlinedIcon
                                    fontSize="small"
                                    color="disabled"
                                  />
                                </IconButton>
                              </Tooltip>
                            )}

                            <Tooltip title="Delete" placement="top">
                              {hasDataStoreEditPermission ? (
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => handleDeleteClick(row.id)}
                                  data-tracker="240"
                                >
                                  <DeleteOutlineOutlinedIcon fontSize="small" />
                                </IconButton>
                              ) : (
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  disabled
                                >
                                  <DeleteOutlineOutlinedIcon
                                    fontSize="small"
                                    color="disabled"
                                  />
                                </IconButton>
                              )}
                            </Tooltip>

                            <Tooltip title="Run Query" placement="top">
                              <IconButton
                                size="small"
                                color="secondary"
                                component={RouterLink}
                                to={{
                                  pathname:
                                    "/admin/queries/run_query",
                                  search: (() => {
                                    const params = new URLSearchParams(
                                      searchParams,
                                    );
                                    params.set("id", row.id);
                                    return params.toString();
                                  })(),
                                }}
                                data-tracker="391"
                              >
                                <PlayCircleOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : key === "modified" ? (
                          row[key] && formatUtcToHumanReadable(row[key] || "")
                        ) : (
                          row[key] || ""
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
            ) : (
              // Display "No data available" row when sortedData is empty
              <TableRow>
                <TableCell
                  colSpan={Object.keys(tableHeadings).length}
                  align="center"
                >
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagination Controls */}
        <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          padding={1}
        >
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={sortedData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                {
                  display: "none",
                },
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-toolbar": {
                paddingLeft: "16px",
              },
            }}
          />

          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={(_, value) => handleChangePage(null, value - 1)}
            showFirstButton
            showLastButton
            color="secondary"
            sx={{
              ".css-wjh20t-MuiPagination-ul": {
                justifyContent: "center",
              },
            }}
            size="small"
          />

          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={queryContent && sortedData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            classes={{
              displayedRows: "hidden-displayed-rows", // Apply the custom class
            }}
            sx={{
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-input": {
                marginRight: "15px",
              },
            }}
          />
        </Stack>

        {/* <EditDataStoreRequestModal /> */}
        <DeleteConfirmationDialog
          open={deleteConfirmOpen}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="Confirm Delete"
          message="Are you sure you want to delete this query? This action cannot be undone."
        />
      </TableContainer>
    </>
  );
};

export default DataStoreRequestTable;
